import { cloneDeep } from 'lodash-es';
import debounce from 'lodash/debounce';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactGA from 'react-ga4';
import styled from 'styled-components';
import { ampli } from '../../../../../../ampli';
import {
  directDealFiltersConstant,
  IDirectDealFilter,
} from '../../../../../../constants/filter/directDealFilter.constant';
import {
  IMarketPriceFilter,
  marketPriceFiltersConstant,
} from '../../../../../../constants/filter/marketPriceFilter.constant';
import { IProductFilter, productFiltersConstant } from '../../../../../../constants/filter/productFilter.constant';
import { filterSliderConstant } from '../../../../../../constants/filter/slider/filterSliderConstant';
import {
  IUserOwnTractorFilter,
  userOwnTractorFiltersConstant,
} from '../../../../../../constants/filter/userOwnTractorFilter.constant';
import { useGetMachinePriceStatisticsMeta } from '../../../../../../hooks/api/marketPrice/queries/useGetMachinePriceStatisticsMeta.query';
import { useGetOpenMarketProductsMetaQuery } from '../../../../../../hooks/api/product/queries/useGetOpenMarketProductsMeta.query';
import { useGetProductsQuery } from '../../../../../../hooks/api/product/queries/useGetProducts.query';
import { useGetUserOwnTractorTotalCountQuery } from '../../../../../../hooks/api/user/queries/useGetUserOwnTractorTotalCount.query';
import { useAppDispatch, useAppSelector } from '../../../../../../hooks/store/useStore';
import {
  FilterOptionValueType,
  FilterType,
  IFilter,
  IFilterOption,
} from '../../../../../../interfaces/Filter.interfaces';
import { filterSlice } from '../../../../../../store/filterSlice';
import { changeFilterOptionIsSelected } from '../../../../../../utils/filter/changeFilterOptionIsSelected.util';
import { changeFilterOptionValue } from '../../../../../../utils/filter/changeFilterOptionValue.util';
import { createDirectDealFilterSearchParams } from '../../../../../../utils/filter/createDirectDealFilterSearchParams.util';
import { createMarketPriceFilterSearchParams } from '../../../../../../utils/filter/createMarketPriceFilterSearchParams.util';
import { createProductFilterSearchParams } from '../../../../../../utils/filter/createProductFilterSearchParams.util';
import { createUserOwnTractorFilterSearchParams } from '../../../../../../utils/filter/createUserOwnTractorFilterSearchParams.util';
import { resetFilterOptionIsSelected } from '../../../../../../utils/filter/resetFilterOptionIsSelected.util';
import { trackMarketPriceFilterEvent } from '../../../../../../utils/filter/trackMarketPriceFilterEvent.util';
import { SpinnerLoader } from '../../../../loading/SpinnerLoader';
import { BrandIconFilter } from './components/BrandIconFilter';
import { FilterInput } from './components/FilterInput';
import { FilterSlider } from './components/FilterSlider';

interface FilterControllerProps {
  filterType: FilterType;
  closeModal: () => void;
}

export const FilterController = ({ filterType, closeModal }: FilterControllerProps) => {
  const dispatch = useAppDispatch();
  const filterStates = useAppSelector((state) => state.filter[filterType]);
  const filterActions = filterSlice.actions;

  const filterConstant = useMemo(() => {
    switch (filterType) {
      case 'productFilter':
        return productFiltersConstant;
      case 'userOwnTractorFilter':
        return userOwnTractorFiltersConstant;
      case 'marketPriceFilter':
        return marketPriceFiltersConstant;
      case 'directDealFilter':
        return directDealFiltersConstant;
      default:
        return [];
    }
  }, [filterType]);

  // 전역 상태로 적용된 필터 값을 깊은 복사로 가져오기 위한 함수
  const getGlobalFilters = () => {
    return cloneDeep(filterStates.globalFilters);
  };

  // 전역 상태로 적용된 필터 값을 지역 상태로 가져와 사용
  const [localFilters, setLocalFilters] = useState<IFilter[]>(getGlobalFilters());
  const [isFilterLoading, setIsFilterLoading] = useState(false);

  const debouncedUpdateFilters = useCallback(
    debounce((newFilters: IFilter[]) => {
      setLocalFilters(newFilters);
      setIsFilterLoading(false);
    }, 500),
    [],
  );

  useEffect(() => {
    // 지역 상태로 관리하는 필터의 업데이트 조건이 충족되면 업데이트 수행
    if (filterStates.haveToRefreshLocalStates) {
      setLocalFilters(getGlobalFilters());
      dispatch(filterActions.changeHaveToRefreshLocalStates({ filterType, value: false }));
    }
  }, [filterStates.haveToRefreshLocalStates]);

  const { data: preProducts, isLoading: isGetPreProductsQueryLoading } = useGetProductsQuery({
    searchParam: createProductFilterSearchParams({ filters: localFilters as IProductFilter[] }),
    options: {
      enabled: filterType === 'productFilter',
    },
  });

  const { data: userOwnTractorTotalCount, isLoading: isGetUserOwnTractorTotalCountQueryLoading } =
    useGetUserOwnTractorTotalCountQuery({
      searchParam: createUserOwnTractorFilterSearchParams({ filters: localFilters as IUserOwnTractorFilter[] }),
      options: {
        enabled: filterType === 'userOwnTractorFilter',
      },
    });

  const { data: marketPriceTotalCount, isLoading: isGetMarketPriceTotalCountQueryLoading } =
    useGetMachinePriceStatisticsMeta({
      searchParam: createMarketPriceFilterSearchParams({ filters: localFilters as IMarketPriceFilter[] }),
      options: {
        enabled: filterType === 'marketPriceFilter',
      },
    });

  const { data: directDealTotalCount, isLoading: isGetDirectDealTotalCountQueryLoading } =
    useGetOpenMarketProductsMetaQuery({
      searchParam: {
        ...createDirectDealFilterSearchParams({ filters: localFilters as IDirectDealFilter[] }),
      },
      options: {
        enabled: filterType === 'directDealFilter',
      },
    });

  // 각 필터 영역의 ref, 필터 추가 시 직접 추가 필요
  const filterRef0 = useRef<HTMLDivElement | null>(null);
  const filterRef1 = useRef<HTMLDivElement | null>(null);
  const filterRef2 = useRef<HTMLDivElement | null>(null);
  const filterRef3 = useRef<HTMLDivElement | null>(null);
  const filterRef4 = useRef<HTMLDivElement | null>(null);
  const filterRef5 = useRef<HTMLDivElement | null>(null);
  const filterRef6 = useRef<HTMLDivElement | null>(null);
  const filterRef7 = useRef<HTMLDivElement | null>(null);
  const filterRef8 = useRef<HTMLDivElement | null>(null);
  const filterRef9 = useRef<HTMLDivElement | null>(null);
  const filterRef10 = useRef<HTMLDivElement | null>(null);

  // 자신(filter)에게 적용된 ref 를 가져올 때 사용
  const getFilterRef = (filterIndex: number) => {
    switch (filterIndex) {
      case 0:
        return filterRef0;
      case 1:
        return filterRef1;
      case 2:
        return filterRef2;
      case 3:
        return filterRef3;
      case 4:
        return filterRef4;
      case 5:
        return filterRef5;
      case 6:
        return filterRef6;
      case 7:
        return filterRef7;
      case 8:
        return filterRef8;
      case 9:
        return filterRef9;
      default:
        return filterRef10;
    }
  };

  // filterRef 들이 나열된 SelectSection 의 Ref, 각 filterRef 의 Header 로 scroll 할 때 사용
  const filterSelectSectionRef = useRef<HTMLDivElement | null>(null);

  const handleFilterMenuButtonClick = (filterIndex: number) => {
    scrollToClickedFilter(filterIndex);
  };

  // SelectSectionRef 의 scrollY 값을 클릭한 메뉴 버튼의 ref header 로 옮기기 위한 함수
  const scrollToClickedFilter = (filterIndex: number) =>
    filterSelectSectionRef.current?.scrollTo({
      top: getFilterRef(filterIndex).current?.offsetTop,
    });

  // SelectSection의 scrollY 값을 관리하는 지역 상태
  const [filterSelectSectionScrollTop, setFilterSelectSectionScrollTop] = useState<number>(0);

  const isConfirmButtonDisabled = useMemo<boolean>(() => {
    if (filterType === 'productFilter') {
      return preProducts?.length === 0 || isGetPreProductsQueryLoading || isFilterLoading;
    }

    if (filterType === 'userOwnTractorFilter') {
      return userOwnTractorTotalCount?.count === 0 || isGetUserOwnTractorTotalCountQueryLoading || isFilterLoading;
    }

    if (filterType === 'marketPriceFilter') {
      return (
        !marketPriceTotalCount?.totalCount ||
        marketPriceTotalCount?.totalCount <= 0 ||
        isGetMarketPriceTotalCountQueryLoading ||
        isFilterLoading
      );
    }

    if (filterType === 'directDealFilter') {
      return (
        !directDealTotalCount?.totalCount ||
        directDealTotalCount?.totalCount <= 0 ||
        isGetDirectDealTotalCountQueryLoading ||
        isFilterLoading
      );
    }

    return false;
  }, [
    filterType,
    preProducts,
    isGetPreProductsQueryLoading,
    userOwnTractorTotalCount,
    isGetUserOwnTractorTotalCountQueryLoading,
    marketPriceTotalCount,
    isGetMarketPriceTotalCountQueryLoading,
    directDealTotalCount,
    isGetDirectDealTotalCountQueryLoading,
    isFilterLoading,
  ]);

  const isBrandWithLogo = (filterKey: string) => {
    return (filterType === 'marketPriceFilter' || filterType === 'directDealFilter') && filterKey === 'filter_brand';
  };

  // machineType에 따라 필터를 비활성화하는 함수
  const isFilterDisabled = (filterKey: string) => {
    if (filterType !== 'directDealFilter') return false;

    // machineType 필터에서 현재 선택된 값을 가져옴
    const machineTypeFilter = localFilters.find((filter) => filter.key === 'machineType');
    const selectedMachineType = machineTypeFilter?.options.find((option) => option.isSelected)?.value;

    if (
      (selectedMachineType === 'combine' || selectedMachineType === 'transplanter') &&
      filterKey === 'engineOutputInPs'
    ) {
      return true;
    }

    if (selectedMachineType === 'tractor' && filterKey === 'rowConfig') {
      return true;
    }

    return false;
  };

  const confirmButtonText = useMemo<string>(() => {
    if (filterType === 'productFilter') {
      return preProducts?.length === 0 ? '매물 없음' : `${preProducts?.length}대 보기`;
    }

    if (filterType === 'userOwnTractorFilter') {
      return `${userOwnTractorTotalCount?.count}대 보기`;
    }

    if (filterType === 'marketPriceFilter') {
      return !marketPriceTotalCount?.totalCount || marketPriceTotalCount?.totalCount <= 0
        ? '매물 없음'
        : `${marketPriceTotalCount?.totalCount}대 보기`;
    }

    if (filterType === 'directDealFilter') {
      return !directDealTotalCount?.totalCount || directDealTotalCount?.totalCount <= 0
        ? '매물 없음'
        : `${directDealTotalCount?.totalCount}대 보기`;
    }

    return '';
  }, [filterType, preProducts, userOwnTractorTotalCount, marketPriceTotalCount, directDealTotalCount]);

  const isConfirmButtonLoading = useMemo<boolean>(() => {
    if (filterType === 'productFilter') {
      return isGetPreProductsQueryLoading || isFilterLoading;
    }

    if (filterType === 'userOwnTractorFilter') {
      return isGetUserOwnTractorTotalCountQueryLoading || isFilterLoading;
    }

    if (filterType === 'marketPriceFilter') {
      return isGetMarketPriceTotalCountQueryLoading || isFilterLoading;
    }

    if (filterType === 'directDealFilter') {
      return isGetDirectDealTotalCountQueryLoading || isFilterLoading;
    }

    return false;
  }, [
    filterType,
    isGetPreProductsQueryLoading,
    isGetUserOwnTractorTotalCountQueryLoading,
    isGetMarketPriceTotalCountQueryLoading,
    isFilterLoading,
    isGetDirectDealTotalCountQueryLoading,
  ]);

  // SelectSection의 scrollY 값을 height 값으로 변경할 때 사용하는 함수
  const changeFilterSelectSectionScrollTop = (height: number) => {
    setFilterSelectSectionScrollTop(height);
  };

  useEffect(() => {
    // SelectSection에서 scroll 이 발생할 때, scrollTop 값을 지역 상태로 등록하기 위한 코드
    filterSelectSectionRef.current?.addEventListener('scroll', () =>
      changeFilterSelectSectionScrollTop(Number(filterSelectSectionRef.current?.scrollTop)),
    );
  }, []);

  useEffect(() => {
    if (typeof filterStates.clickedFilterButtonIndex === 'number') {
      // 슬라이드 모달이 화면 하단에 들어가 있는 상태에서 offsetTop 값이 0 이 되는 현상이 있음
      // 따라서 슬라이드 모달가 올라오면서 offsetTop 제대로 걸린 이후에 스크롤을 수행하도록 setTimeout 설정함
      setTimeout(() => {
        scrollToClickedFilter(filterStates.clickedFilterButtonIndex as number);
      }, 200);

      dispatch(filterActions.changeClickedFilterButtonIndex({ filterType, value: undefined }));
    }
  }, [filterStates.clickedFilterButtonIndex]);

  // 현재 SelectSectionRef에서의 scroll 위치와 필터 별 Ref 위치를 비교하여, 각 필터 메뉴의 활성화 여부를 관리하는 코드
  // (1개의 필터 메뉴만 활성화될 수 있음)
  const isFilterMenuActived = useMemo<boolean[]>(() => {
    const filterMenuActived = [false, false, false, false];

    const PIXELS_FOR_ACTIVE_MENU = 30;

    const filterRef1Height = Number(filterRef1.current?.offsetTop) - PIXELS_FOR_ACTIVE_MENU;
    const filterRef2Height = Number(filterRef2.current?.offsetTop) - PIXELS_FOR_ACTIVE_MENU;
    const filterRef3Height = Number(filterRef3.current?.offsetTop) - PIXELS_FOR_ACTIVE_MENU;
    const filterRef4Height = Number(filterRef4.current?.offsetTop) - PIXELS_FOR_ACTIVE_MENU;
    const filterRef5Height = Number(filterRef5.current?.offsetTop) - PIXELS_FOR_ACTIVE_MENU;
    const filterRef6Height = Number(filterRef6.current?.offsetTop) - PIXELS_FOR_ACTIVE_MENU;
    const filterRef7Height = Number(filterRef7.current?.offsetTop) - PIXELS_FOR_ACTIVE_MENU;
    const filterRef8Height = Number(filterRef8.current?.offsetTop) - PIXELS_FOR_ACTIVE_MENU;
    const filterRef9Height = Number(filterRef9.current?.offsetTop) - PIXELS_FOR_ACTIVE_MENU;
    const filterRef10Height = Number(filterRef10.current?.offsetTop) - PIXELS_FOR_ACTIVE_MENU;

    // 네 번째 필터 메뉴 활성화 여부 계산
    // (마지막 필터 메뉴 요소부터 역으로 계산해야 함
    // 각 필터의 height 값이 500, 1000, 1500 이고 scrollY 값이 1100 인 경우,
    // 1100 > 500 으로 첫 번째 메뉴 버튼이 활성화 됨.
    // 따라서 가장 밑에 있는 필터 메뉴부터 height 값을 계산함)
    if (filterSelectSectionScrollTop >= filterRef10Height) {
      filterMenuActived[10] = true;
      return filterMenuActived;
    }

    if (filterSelectSectionScrollTop >= filterRef9Height) {
      filterMenuActived[9] = true;
      return filterMenuActived;
    }

    if (filterSelectSectionScrollTop >= filterRef8Height) {
      filterMenuActived[8] = true;
      return filterMenuActived;
    }

    if (filterSelectSectionScrollTop >= filterRef7Height) {
      filterMenuActived[7] = true;
      return filterMenuActived;
    }

    if (filterSelectSectionScrollTop >= filterRef6Height) {
      filterMenuActived[6] = true;
      return filterMenuActived;
    }

    if (filterSelectSectionScrollTop >= filterRef5Height) {
      filterMenuActived[5] = true;
      return filterMenuActived;
    }

    if (filterSelectSectionScrollTop >= filterRef4Height) {
      filterMenuActived[4] = true;
      return filterMenuActived;
    }

    if (filterSelectSectionScrollTop >= filterRef3Height) {
      filterMenuActived[3] = true;
      return filterMenuActived;
    }

    // 세 번째 필터 메뉴 활성화 여부 계산
    if (filterSelectSectionScrollTop >= filterRef2Height) {
      filterMenuActived[2] = true;
      return filterMenuActived;
    }

    // 두 번째 필터 메뉴 활성화 여부 계산
    if (filterSelectSectionScrollTop >= filterRef1Height) {
      filterMenuActived[1] = true;
      return filterMenuActived;
    }

    // 위 조건문에 모두 걸리지 않는 경우, 기본으로 첫번째 필터 메뉴를 활성화 함
    filterMenuActived[0] = true;
    return filterMenuActived;
  }, [filterSelectSectionScrollTop]);

  // 필터 Input (radio / checkbox) 클릭 시 해당 input 의 type 에 맞게 지역 상태가 갖고 있는 필터 옵션의 isSelected 값을 변경함
  const handleFilterInputClick = (params: { filter: IFilter; option: IFilterOption }) => {
    if (filterType === 'productFilter') {
      ampli.pvmClickProductFilterOptionInput({
        filter_key: params.filter.key,
        filter_option_name: params.option.optionName,
      });
      ReactGA.event('pvm_click_product_filter_option_input', {
        filter_key: params.filter.key,
        filter_option_name: params.option.optionName,
      });
    }

    const newFilters = changeFilterOptionIsSelected({
      currentFilters: localFilters,
      targetFilter: params.filter,
      targetOption: params.option,
    });

    setLocalFilters(newFilters as IFilter[]);
  };

  const handleFilterSliderChange = (params: { filter: IFilter; optionValues: FilterOptionValueType[] }) => {
    setIsFilterLoading(true);
    const newFilters = changeFilterOptionValue({
      currentFilters: localFilters,
      targetFilter: params.filter,
      newOptionValues: params.optionValues,
    });

    debouncedUpdateFilters(newFilters as IFilter[]);
  };

  // 초기화 버튼 클릭 시 지역 상태가 갖고 있는 필터 옵션의 isSelected 값을 초기화함 (전역 상태와 동일하게 X, 아예 처음으로 필터 값으로 되돌림)
  const handleResetLocalFiltersButtonClick = () => {
    ampli.pvmClickFilterControllerResetButton({ filter_type: filterType, filters: [...localFilters] });

    setLocalFilters(() => {
      const newFilters = resetFilterOptionIsSelected({ currentFilters: localFilters });
      return newFilters as IFilter[];
    });
  };

  // 모든 필터에 대해 활성화된 필터가 있는지 확인
  const hasAnyActiveFilter = useMemo(() => {
    return localFilters.some((filter) => {
      let isFilterButtonActive = false;

      if (filter.optionType === 'radio' || filter.optionType === 'checkbox') {
        filter.options.forEach((option) => {
          if (option.isSelected && !option.isDefault) {
            isFilterButtonActive = true;
          }
        });
      } else if (filter.optionType === 'slider') {
        filter.options.forEach((option) => {
          if (option.value !== option.defaultValue) {
            isFilterButtonActive = true;
          }
        });
      }

      return isFilterButtonActive;
    });
  }, [localFilters]);

  // n대 보기(== 적용하기) 버튼 클릭 시 지역 상태의 값을 전역 상태로 반영하고, 모달을 닫음 => 필터 적용
  const handleApplyFilterButtonClick = () => {
    if (filterType === 'userOwnTractorFilter') {
      const filters = localFilters as IUserOwnTractorFilter[];

      const property = {
        filter_sort:
          filters.find((filter) => filter.key === 'sorter')?.options.find((option) => option.isSelected)?.optionName ??
          '',
        filter_brands:
          filters
            .find((filter) => filter.key === 'filter_brand')
            ?.options.filter((option) => option.isSelected)
            .map((option) => option.optionName)
            .join(',') ?? '',
        filter_engine_output_in_ps:
          filters
            .find((filter) => filter.key === 'engineOutputInPs')
            ?.options.filter((option) => option.isSelected)
            .map((option) => option.value)
            .join(',') ?? '',
        filter_region:
          filters
            .find((filter) => filter.key === 'filter_region')
            ?.options.filter((option) => option.isSelected)
            .map((option) => option.optionName)
            .join(',') ?? '',
      };

      ampli.pvmClickPokeFilterSubmit({ ...property });
      ReactGA.event('pvm_click_poke_filter_submit', { ...property });
    }

    if (filterType === 'marketPriceFilter') {
      trackMarketPriceFilterEvent(localFilters as IMarketPriceFilter[], filterStates.isAutoOpened);
    }

    ampli.pvmClickFilterControllerSubmitButton({
      filter_type: filterType,
      is_any_filter_selected: hasAnyActiveFilter,
      filters: [...localFilters],
    });

    dispatch(filterActions.changeFilters({ filterType, filters: localFilters }));
    dispatch(filterActions.changeHaveToRefreshLocalStates({ filterType, value: true }));
    if (filterType === 'marketPriceFilter') {
      dispatch(filterActions.setIsAutoOpened({ filterType, value: false }));
    }
    window.scrollTo(0, 0);
    closeModal();
  };

  return (
    <Container>
      <FilterMenuSection>
        {filterConstant.map((filter, index) => (
          <FilterMenuButton
            key={`menu-${filter.key}-${filter.title}`}
            isActive={isFilterMenuActived[index]}
            onClick={() => {
              handleFilterMenuButtonClick(index);
              ampli.pvmClickFilterControllerMenuButton({
                filter_type: filterType,
                filter_key: filter.key,
              });
            }}
          >
            <FilterMenuButtonText>{filter.title}</FilterMenuButtonText>
          </FilterMenuButton>
        ))}
      </FilterMenuSection>

      <FilterSelectSection ref={filterSelectSectionRef}>
        {localFilters.map((filter, index) => {
          const isDisabled = isFilterDisabled(filter.key);

          return (
            <FilterWrap
              key={`title-${filter.key}-${filter.title}`}
              ref={getFilterRef(index)}
              isFirst={index === 0}
              isLast={index === localFilters.length - 1}
              isDisabled={isDisabled}
            >
              <FilterTitleWrap>
                <FilterTitle isDisabled={isDisabled}>{filter.title}</FilterTitle>
              </FilterTitleWrap>
              {isBrandWithLogo(filter.key) && (
                <IconFilterContainer isTwoItems={filter.options.length === 2}>
                  {filter.options.map((option) => (
                    <BrandIconFilter
                      key={`${filter.key}-${option.optionName}-${option.value}`}
                      filter={filter}
                      filterType={filterType}
                      option={option}
                      handleClick={() => !isDisabled && handleFilterInputClick({ filter, option })}
                      localFilters={localFilters}
                      isDisabled={isDisabled}
                    />
                  ))}
                </IconFilterContainer>
              )}

              {!isBrandWithLogo(filter.key) &&
                filter.optionType !== 'slider' &&
                filter.options.map((option) => (
                  <FilterInput
                    key={`${filter.key}-${option.optionName}-${option.value}`}
                    filter={filter}
                    filterType={filterType}
                    option={option}
                    handleClick={() => !isDisabled && handleFilterInputClick({ filter, option })}
                    localFilters={localFilters}
                    isDisabled={isDisabled}
                  />
                ))}

              {filter.optionType === 'slider' && filter.key in filterSliderConstant && (
                <FilterSlider
                  options={filter.options}
                  handleChange={(newOptionValues: FilterOptionValueType[]) =>
                    !isDisabled && handleFilterSliderChange({ filter, optionValues: newOptionValues })
                  }
                  sliderConstant={filterSliderConstant[filter.key as keyof typeof filterSliderConstant]}
                  isDisabled={isDisabled}
                />
              )}
            </FilterWrap>
          );
        })}
      </FilterSelectSection>

      <ButtonContainer>
        <BlurSection />

        <GreyButton onClick={handleResetLocalFiltersButtonClick}>
          <GreyButtonText>{'초기화'}</GreyButtonText>
        </GreyButton>
        <ConfirmButton disabled={isConfirmButtonDisabled} onClick={handleApplyFilterButtonClick}>
          {!isConfirmButtonLoading && <ConfirmButtonText>{confirmButtonText}</ConfirmButtonText>}
          <SpinnerLoader isVisible={isConfirmButtonLoading} />
        </ConfirmButton>
      </ButtonContainer>
    </Container>
  );
};

const Container = styled.div`
  height: 700px;

  display: flex;
  align-items: flex-start;
  align-self: stretch;

  background: var(--Light-Scheme-Monochrome-grey10, #f2f2f7);

  position: relative;
`;

const FilterMenuSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;
`;

const FilterMenuButton = styled.button<{ isActive: boolean }>`
  width: 80px;
  height: 48px;

  display: flex;
  justify-content: center;
  align-items: center;

  padding: 16px;
  gap: 10px;

  border-radius: var(--radius-none, 0px);
  background: ${(props) => props.isActive && '#FFF'};
`;

const FilterMenuButtonText = styled.span`
  color: var(--Light-Scheme-Monochrome-black, #232429);

  /* Display/Medium/m-regular */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 130%; /* 18.2px */

  white-space: nowrap;
`;

const FilterSelectSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1 0 0;
  align-self: stretch;

  background: var(--Light-Scheme-Monochrome-white, #fff);

  overflow-y: scroll;

  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
  ::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera*/
  }
`;

const FilterWrap = styled.div<{ isFirst: boolean; isLast: boolean; isDisabled?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  align-self: stretch;

  padding-top: ${(props) => !props.isFirst && '16px'};
  padding-bottom: ${(props) => (props.isLast ? '520px' : '16px')};

  position: relative;
  opacity: ${(props) => (props.isDisabled ? 0.5 : 1)};
  pointer-events: ${(props) => (props.isDisabled ? 'none' : 'auto')};

  border-bottom: ${(props) => !props.isLast && '1px solid var(--Light-Scheme-Monochrome-grey20, #e4e4ed)'};
`;

const FilterTitleWrap = styled.div`
  height: 48px;

  display: flex;
  align-items: center;
  align-self: stretch;

  padding: 0px 24px;
  gap: 10px;
`;

const FilterTitle = styled.span<{ isDisabled?: boolean }>`
  flex: 1 0 0;

  color: ${(props) =>
    props.isDisabled
      ? 'var(--Light-Scheme-Monochrome-grey100, #858894)'
      : 'var(--Light-Scheme-Monochrome-black, #232429)'};

  /* Display/Large/l-bold */
  font-family: Pretendard;
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 130%; /* 20.8px */
`;

const IconFilterContainer = styled.div<{ isTwoItems?: boolean }>`
  width: 100%;

  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
  justify-items: center;
  padding: 8px 24px;

  /* display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  padding: 8px 24px;
  gap: 8px; */
`;

const FilterOptionWrap = styled.div`
  display: flex;
  align-items: center;
  align-self: stretch;

  padding: var(--radius-large, 16px) 24px var(--radius-large, 16px) 40px;

  border-radius: var(--radius-medium, 8px);
`;

const BlurSection = styled.div`
  position: absolute;
  left: 0px;
  top: -40px;

  width: 100%;
  max-width: 512px;
  height: 40px;

  display: flex;
  padding: var(--radius-large, 16px);
  justify-content: center;
  align-items: center;
  gap: var(--radius-small, 4px);

  border-radius: var(--radius-none, 0px);
  background: linear-gradient(180deg, rgba(255, 255, 255, 0) 0%, #fff 63.96%);
`;

const ButtonContainer = styled.div`
  position: fixed;

  left: 0;
  bottom: 0;

  width: 100%;

  display: flex;
  align-items: center;
  align-self: stretch;

  padding: 0px 24px 32px 24px;
  gap: 10px;

  background-color: white;
`;

const GreyButton = styled.button`
  max-height: 40px;

  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1 0 0;

  padding: 16px;
  gap: 4px;

  border-radius: var(--radius-medium, 8px);
  background: var(--Light-Scheme-Monochrome-grey10, #f2f2f7);
`;

const GreyButtonText = styled.span`
  color: var(--Light-Scheme-Monochrome-grey100, #858894);

  /* System/Medium/m-medium */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 85%; /* 11.9px */
`;

const ConfirmButton = styled.button`
  height: 40px;

  display: flex;
  justify-content: center;
  align-items: center;
  flex: 1 0 0;

  padding: 16px;
  gap: 4px;

  border-radius: var(--radius-medium, 8px);
  background: var(--Light-Scheme-Color-primary-blue, #196eff);

  &:disabled {
    opacity: 0.3;
  }
`;

const ConfirmButtonText = styled.span`
  color: var(--Light-Scheme-Monochrome-white, #fff);

  /* System/Medium/m-medium */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 85%; /* 11.9px */
`;
